import { cn } from '@/lib/utils';
import type { BrandAwareLoadingScope } from '@/lib/branding/resolveBrandAwareLoading';

export type LoadingVisualLogoSize = 'sm' | 'md' | 'lg';
export type LoadingVisualAnimationType = 'default' | 'minimal' | 'pulse' | 'dots' | 'indicator';
export type LoadingVisualVariant = 'full' | 'compact';
export type LoadingGraphicSize = 'inline' | 'content' | 'fullscreen';

const dotSizeClasses: Record<LoadingGraphicSize, string> = {
  inline: 'h-1.5 w-1.5',
  content: 'h-2.5 w-2.5',
  fullscreen: 'h-2.5 w-2.5',
};

const pulseWrapSizeClasses: Record<LoadingGraphicSize, string> = {
  inline: 'h-4 w-4',
  content: 'h-9 w-9',
  fullscreen: 'h-9 w-9',
};

const defaultWrapSizeClasses: Record<LoadingGraphicSize, string> = {
  inline: 'h-4 w-4',
  content: 'h-10 w-10',
  fullscreen: 'h-10 w-10',
};

const brandAssetSizeClasses: Record<LoadingGraphicSize, string> = {
  inline: 'h-4 w-4',
  content: 'h-7 w-7 sm:h-8 sm:w-8',
  fullscreen: 'h-12 w-12 sm:h-16 sm:w-16',
};

export interface LoadingVisualCoreProps {
  animationType?: LoadingVisualAnimationType;
  durationMs?: number;
  graphicSize?: LoadingGraphicSize;
  animationsEnabled?: boolean;
  fillClassName?: string;
  className?: string;
}

/**
 * Core animation graphic — dots/pulse/default/minimal only.
 */
export function LoadingVisualCore({
  animationType = 'default',
  durationMs = 1000,
  graphicSize = 'content',
  animationsEnabled = true,
  fillClassName = 'bg-primary',
  className,
}: LoadingVisualCoreProps) {
  const durS = durationMs / 1000;
  const dotClass = dotSizeClasses[graphicSize];
  const gapClass = graphicSize === 'inline' ? 'gap-1.5' : 'gap-2';

  if (animationType === 'dots') {
    const dotDur = Math.max(0.2, durS * 0.6);
    return (
      <div className={cn('inline-flex items-center', gapClass, className)} aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={cn(dotClass, 'animate-bounce rounded-full', fillClassName)}
            style={
              animationsEnabled
                ? { animationDuration: `${dotDur}s`, animationDelay: `${dotDur * i * 0.25}s` }
                : undefined
            }
          />
        ))}
      </div>
    );
  }

  if (animationType === 'pulse') {
    const wrapClass = pulseWrapSizeClasses[graphicSize];
    return (
      <div className={cn('relative inline-flex items-center justify-center', wrapClass, className)} aria-hidden="true">
        {animationsEnabled ? (
          <div
            className="absolute inset-0 animate-ping rounded-full bg-primary/15"
            style={{ animationDuration: `${Math.max(0.2, durS)}s` }}
          />
        ) : null}
        <div
          className={cn(
            'relative rounded-full border border-primary/35 bg-primary/20',
            graphicSize === 'inline' ? 'h-2 w-2' : 'h-5 w-5',
            animationsEnabled ? 'animate-pulse' : undefined,
          )}
          style={animationsEnabled ? { animationDuration: `${Math.max(0.2, durS)}s` } : undefined}
        />
      </div>
    );
  }

  if (animationType === 'minimal') {
    return (
      <span
        className={cn(dotClass, 'inline-block rounded-full', fillClassName, className)}
        aria-hidden="true"
      />
    );
  }

  const defaultWrap = defaultWrapSizeClasses[graphicSize];
  return (
    <div className={cn('relative inline-flex items-center justify-center', defaultWrap, className)} aria-hidden="true">
      {animationsEnabled ? (
        <div
          className="absolute inset-0 -m-2 animate-pulse rounded-full border border-primary/20"
          style={{ animationDuration: `${Math.max(0.2, durS)}s` }}
        />
      ) : null}
      <span className={cn(dotClass, 'relative rounded-full', fillClassName, animationsEnabled ? 'animate-pulse' : undefined)} />
    </div>
  );
}

function BrandAssetImage({
  src,
  graphicSize,
  animationsEnabled,
  animationType,
  durationMs,
  alt,
}: {
  src: string;
  graphicSize: LoadingGraphicSize;
  animationsEnabled: boolean;
  animationType: LoadingVisualAnimationType;
  durationMs: number;
  alt: string;
}) {
  const sizeClass = brandAssetSizeClasses[graphicSize];
  const durS = Math.max(0.2, durationMs / 1000);
  const pingOnAsset = animationType === 'default';
  const pulseOnAsset = animationType === 'pulse';

  if (pingOnAsset && animationsEnabled && graphicSize !== 'inline') {
    return (
      <div className={cn('relative inline-flex items-center justify-center', sizeClass)} aria-hidden="true">
        <div
          className="absolute inset-0 animate-ping rounded-full bg-primary/10"
          style={{ animationDuration: `${durS}s` }}
        />
        <img
          src={src}
          alt={alt}
          className={cn('relative max-h-full max-w-full object-contain', sizeClass)}
          draggable={false}
        />
      </div>
    );
  }

  if (pulseOnAsset && animationsEnabled && graphicSize !== 'inline') {
    return (
      <div className={cn('relative inline-flex items-center justify-center', sizeClass)} aria-hidden="true">
        <div
          className="absolute inset-0 animate-ping rounded-full bg-primary/15"
          style={{ animationDuration: `${durS}s` }}
        />
        <img
          src={src}
          alt={alt}
          className={cn('relative max-h-full max-w-full object-contain', sizeClass)}
          draggable={false}
        />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={cn('object-contain', sizeClass)}
      draggable={false}
      aria-hidden="true"
    />
  );
}

export interface LoadingVisualProps {
  appName?: string;
  subtitle?: string;
  logoSize?: LoadingVisualLogoSize;
  animationType?: LoadingVisualAnimationType;
  durationMs?: number;
  showProgress?: boolean;
  variant?: LoadingVisualVariant;
  animationsEnabled?: boolean;
  spinnerLabel?: string;
  pulseLogo?: boolean;
  logoAlt?: string;
  showAppName?: boolean;
  graphicSize?: LoadingGraphicSize;
  fillClassName?: string;
  showStatusLabel?: boolean;
  statusLabel?: string;
  className?: string;
  brandAssetUrl?: string | null;
  showBrandAsset?: boolean;
  showTitle?: boolean;
  glassEnabled?: boolean;
  scope?: BrandAwareLoadingScope;
  /** runtime = flat professional loader; preview = config UI may wrap externally */
  presentation?: 'runtime' | 'preview';
}

/**
 * Brand-aware configured loading visual — asset + optional title + animation.
 */
export function LoadingVisual({
  animationType = 'default',
  durationMs = 1000,
  variant = 'full',
  animationsEnabled = true,
  spinnerLabel,
  graphicSize = 'content',
  fillClassName = 'bg-primary',
  showStatusLabel = false,
  statusLabel = 'Đang tải',
  className,
  appName,
  subtitle: _subtitle,
  logoSize: _logoSize,
  showProgress: _showProgress = false,
  pulseLogo: _pulseLogo,
  logoAlt,
  showAppName: _showAppName = false,
  brandAssetUrl,
  showBrandAsset = false,
  showTitle = false,
  glassEnabled = false,
  scope = 'content',
  presentation = 'runtime',
}: LoadingVisualProps) {
  void variant;

  const resolvedTitle = (appName?.trim() || '').length > 0 ? appName!.trim() : null;
  const resolvedAsset = brandAssetUrl ?? null;
  const shouldShowAsset = showBrandAsset && !!resolvedAsset;
  const shouldShowTitle = showTitle && !!resolvedTitle;
  const assetAlt = logoAlt || resolvedTitle || 'Loading';

  const showAnimationBelow =
    animationType === 'dots' ||
    animationType === 'minimal' ||
    (animationType === 'pulse' && !shouldShowAsset);

  const resolvedLabel = spinnerLabel || statusLabel;

  if (animationType === 'indicator') {
    return (
      <div className={cn('flex flex-col items-center gap-3', className)}>
        <LoadingVisualCore
          animationType="default"
          durationMs={durationMs}
          graphicSize="inline"
          animationsEnabled={animationsEnabled}
          fillClassName={fillClassName}
        />
        <span className="sr-only">{resolvedLabel}</span>
      </div>
    );
  }

  const stackGap = graphicSize === 'fullscreen' ? 'gap-3' : graphicSize === 'content' ? 'gap-2' : 'gap-1.5';

  const inner = (
    <div className={cn('flex flex-col items-center text-center', stackGap, className)}>
      {shouldShowAsset && resolvedAsset ? (
        <BrandAssetImage
          src={resolvedAsset}
          graphicSize={graphicSize}
          animationsEnabled={animationsEnabled}
          animationType={animationType}
          durationMs={durationMs}
          alt={assetAlt}
        />
      ) : null}

      {shouldShowTitle && resolvedTitle ? (
        <p
          className={cn(
            'font-medium text-foreground',
            graphicSize === 'fullscreen' ? 'text-base sm:text-lg' : 'text-xs sm:text-sm',
          )}
        >
          {resolvedTitle}
        </p>
      ) : null}

      {showAnimationBelow ? (
        <LoadingVisualCore
          animationType={animationType}
          durationMs={durationMs}
          graphicSize={graphicSize}
          animationsEnabled={animationsEnabled}
          fillClassName={fillClassName}
        />
      ) : null}

      {showStatusLabel ? (
        <p className="text-sm text-muted-foreground">{resolvedLabel}</p>
      ) : (
        <span className="sr-only">{resolvedLabel}</span>
      )}
    </div>
  );

  // Runtime loaders must never use bordered glass cards (UX repair-11).
  void glassEnabled;
  void presentation;
  void scope;

  return inner;
}
